import { FilePdfOutlined, FileTextOutlined, LeftOutlined, ReloadOutlined } from "@ant-design/icons";
import { getClientEnv } from "@shared/env";
import { useQuery } from "@tanstack/react-query";
import { Button, message, Spin } from "antd";
import { useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { getSaleById } from "@/modules/sales/services/sales.backend";
import { formatMoney } from "@/shared/utils/money";
import {
	type ContractFormat,
	type GenerateContractResponse,
	generateContract,
} from "../services/contracts.api";

function getPriceWords(value: number) {
	if (value >= 600_000_000) {
		return "olti yuz yigirma to'qqiz million besh yuz ming";
	}
	if (value >= 400_000_000) {
		return "to'rt yuz sakson million";
	}
	return "uch yuz ellik million";
}

export function ContractPage() {
	const navigate = useNavigate();
	const location = useLocation();
	const [searchParams] = useSearchParams();
	const saleId = searchParams.get("saleId");
	const clientId = searchParams.get("clientId");

	const { data: sale, isLoading } = useQuery({
		queryKey: ["sale", saleId],
		queryFn: () => getSaleById(saleId!),
		enabled: !!saleId,
	});

	const [generatingFormat, setGeneratingFormat] = useState<ContractFormat | null>(null);
	const [contracts, setContracts] = useState<
		Partial<Record<ContractFormat, GenerateContractResponse>>
	>({});

	const backPath = clientId ? `/clients/${clientId}` : saleId ? `/sales/${saleId}` : "/sales";
	const latestContract = contracts.pdf;
	const contractFileName =
		latestContract?.fileName ?? `contract_${sale?.saleNumber ?? "draft"}.pdf`;
	const backLabel = saleId
		? "Shartnomaga qaytish"
		: clientId
			? "Mijoz kartochkasiga qaytish"
			: "Ortga";

	const handleGenerate = async (format: ContractFormat, force = false) => {
		if (!sale?.id) {
			message.warning("Sotuv topilmadi");
			return;
		}
		setGeneratingFormat(format);
		try {
			const result = await generateContract(sale.id, format, force);
			setContracts((current) => ({ ...current, [format]: result }));
			message.success(`Shartnoma ${format.toUpperCase()} formatda generatsiya qilindi`);
		} catch {
			message.error("Shartnoma generatsiyasida xatolik yuz berdi");
		} finally {
			setGeneratingFormat(null);
		}
	};

	const handleDownload = (format: ContractFormat) => {
		const generatedContract = contracts[format];
		if (generatedContract?.url) {
			// generatedContract.url is a path (e.g. "/api/contracts/download/...") relative to the
			// backend, not the frontend origin — resolve it against VITE_API_URL like apiClient does,
			// since window.open() otherwise resolves it against the current page's origin.
			window.open(new URL(generatedContract.url, getClientEnv().VITE_API_URL).toString(), "_blank");
		} else {
			handleGenerate(format);
		}
	};

	const isGenerating = generatingFormat !== null;

	if (isLoading) {
		return (
			<div style={{ padding: 100, textAlign: "center" }}>
				<Spin size="large" />
			</div>
		);
	}

	if (!sale && saleId) {
		return (
			<div style={{ padding: 100, textAlign: "center" }}>
				<div style={{ fontSize: 14, color: "var(--crm-ink-3)" }}>Sotuv topilmadi</div>
			</div>
		);
	}

	return (
		<div className="crm-contract-page" style={{ padding: 0 }}>
			<div style={{ marginBottom: "6px" }}>
				<Button
					className="crm-back-link"
					icon={<LeftOutlined />}
					onClick={() => navigate(backPath, { state: location.state })}
					type="text"
					style={{
						paddingLeft: 0,
						height: "auto",
						display: "inline-flex",
						alignItems: "center",
						color: "#64748b",
						fontSize: "13px",
					}}
				>
					{backLabel}
				</Button>
			</div>

			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					marginBottom: "16px",
					flexWrap: "wrap",
					gap: "16px",
				}}
			>
				<h1 style={{ margin: 0, fontSize: "28px", fontWeight: 800 }}>
					Shartnoma #{sale?.saleNumber ?? "-"}
				</h1>
				<div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
					{latestContract ? (
						<span
							style={{
								background: "#e8f5e9",
								color: "#2e7d32",
								padding: "4px 12px",
								borderRadius: "100px",
								fontSize: "13px",
								fontWeight: 600,
							}}
						>
							Avtomatik yaratildi
						</span>
					) : null}
					<Button
						icon={<FilePdfOutlined />}
						loading={generatingFormat === "pdf"}
						onClick={() => handleDownload("pdf")}
						style={{
							height: "36px",
							padding: "0 16px",
							borderRadius: "8px",
							fontSize: "13.5px",
							display: "inline-flex",
							alignItems: "center",
						}}
					>
						PDF
					</Button>
				</div>
			</div>

			<div className="crm-contract-grid" style={{ marginTop: "8px" }}>
				<section className="crm-card">
					<div className="crm-card-head">
						<h3>Hujjat ko'rinishi</h3>
					</div>
					<div className="crm-card-body">
						{isGenerating ? (
							<div style={{ textAlign: "center", padding: "60px 0" }}>
								<Spin tip="Shartnoma generatsiya qilinmoqda..." />
							</div>
						) : (
							<div
								className="crm-contract-preview"
								style={{
									border: "2px dashed #cbd5e1",
									borderRadius: "8px",
									padding: "36px 24px",
									display: "flex",
									flexDirection: "column",
									alignItems: "center",
									justifyContent: "center",
									gap: "10px",
									background: "#f8fafc",
									color: "#64748b",
									minHeight: "220px",
								}}
							>
								<FileTextOutlined style={{ fontSize: "40px", color: "#94a3b8" }} />
								<strong style={{ fontSize: "14px", color: "#334155" }}>{contractFileName}</strong>
								<span style={{ fontSize: "12px", color: "#94a3b8" }}>
									12 band + 2 ilova + to'lov jadvali
								</span>
							</div>
						)}
						<div style={{ marginTop: "16px" }}>
							<Button
								icon={<ReloadOutlined />}
								onClick={() => handleGenerate(latestContract?.format ?? "pdf", true)}
								loading={isGenerating}
								style={{ width: "100%" }}
							>
								Qayta generatsiya
							</Button>
						</div>
					</div>
				</section>

				<section className="crm-card">
					<div className="crm-card-head">
						<h3>To'ldirilgan maydonlar</h3>
					</div>
					<div className="crm-card-body crm-contract-fields" style={{ gap: "10px" }}>
						<div style={{ paddingBottom: "10px" }}>
							<span title="{{client_full_name}}">Mijoz ismi (F.I.Sh.)</span>
							<strong>{sale?.clientName ?? "-"}</strong>
						</div>
						<div style={{ paddingBottom: "10px" }}>
							<span title="{{building_name}}">Bino nomi</span>
							<strong>{sale?.buildingName ?? "-"}</strong>
						</div>
						<div style={{ paddingBottom: "10px" }}>
							<span title="{{room_number}} / {{room_floor}}">Xona / Qavat</span>
							<strong>
								{sale?.roomNumber ?? "-"} / {sale?.floor ?? "-"}
							</strong>
						</div>
						<div style={{ paddingBottom: "10px" }}>
							<span title="{{room_area}}">Xona maydoni</span>
							<strong>{sale?.area ?? "-"} m²</strong>
						</div>
						<div style={{ paddingBottom: "10px" }}>
							<span title="{{contract_price_number}}">Shartnoma summasi</span>
							<strong>{sale ? formatMoney(sale.finalPrice) : "-"} UZS</strong>
						</div>
						<div style={{ paddingBottom: "10px" }}>
							<span title="{{contract_price_words}}">Summa so'z bilan</span>
							<strong>{sale ? getPriceWords(sale.finalPrice) : "-"}</strong>
						</div>
						<div style={{ paddingBottom: "10px" }}>
							<span title="{{monthly_payment}} × {{months}}">Oylik to'lov × Muddat</span>
							<strong>
								{sale ? formatMoney(sale.monthlyPayment) : "-"} × {sale?.monthsCount ?? "-"} oy
							</strong>
						</div>
						<div style={{ paddingBottom: "10px" }}>
							<span title="{{org_director}} / {{org_inn}}">Tashkilot rahbari / STIR (INN)</span>
							<strong>—</strong>
						</div>
					</div>
					<div style={{ padding: "0 24px 24px" }}>
						<div
							style={{
								background: "#f8fafc",
								border: "1px solid #e2e8f0",
								padding: "16px",
								borderRadius: "12px",
								color: "#64748b",
								fontSize: "13px",
							}}
						>
							Org rekvizitlari + mijoz pasport + to'lov jadvali (1-ilova) avtomatik to'ldiriladi.
						</div>
					</div>
				</section>
			</div>
		</div>
	);
}

export default ContractPage;
